import { App, Checkbox, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import {
  type PortalTextOffer,
  getPortalTextOffers,
  setPortalTextOffer
} from "../api/services.ts"

export const PortalTextOffers = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [offers, setOffers] = useState<PortalTextOffer[] | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void getPortalTextOffers().then((response) => {
      if (cancelled) return
      setOffers(response.ok && response.data ? response.data.results : [])
    })
    return () => {
      cancelled = true
    }
  }, [])

  const change = async (offer: PortalTextOffer, optedIn: boolean) => {
    setSaving(offer.customer)
    const response = await setPortalTextOffer(offer.customer, optedIn)
    setSaving(null)
    if (!response.ok) {
      message.error(t("portal.settings.textOffers.failed"))
      return
    }
    const saved = unwrapEnvelope<PortalTextOffer>(response.data)
    setOffers((current) =>
      (current ?? []).map((item) =>
        item.customer === saved.customer ? saved : item
      )
    )
  }

  if (offers === null) return <Skeleton active paragraph={{ rows: 1 }} />
  if (offers.length === 0) return null

  return (
    <section
      aria-label={t("portal.settings.textOffers.title")}
      className="flex flex-col gap-2"
    >
      <p className="text-sm font-medium text-gray-800">
        {t("portal.settings.textOffers.title")}
      </p>
      {offers.map((offer) => (
        <div key={offer.customer} className="flex flex-col gap-1">
          <Checkbox
            checked={offer.opted_in}
            disabled={offer.stopped || saving === offer.customer}
            onChange={(event) => void change(offer, event.target.checked)}
          >
            {t("portal.settings.textOffers.label", { company: offer.company })}
          </Checkbox>
          <Typography.Text type="secondary" className="pl-6 text-xs">
            {offer.stopped
              ? t("portal.settings.textOffers.stopped", {
                  company: offer.company
                })
              : t("portal.settings.textOffers.disclosure")}
          </Typography.Text>
        </div>
      ))}
    </section>
  )
}
